import React, { useState } from 'react';

// ── Sidebar navigation structure ─────────────────────────────────────────────

const NAV = [
  {
    section: 'Getting Started',
    items: [
      { id: 'introduction', label: 'Introduction' },
      { id: 'key-concepts', label: 'Key Concepts' },
      { id: 'roles', label: 'Roles & Permissions' },
    ],
  },
  {
    section: 'Risk Management',
    items: [
      { id: 'risk-registration', label: 'Risk Registration' },
      { id: 'risk-assessment', label: 'Risk Assessment' },
      { id: 'risk-response', label: 'Risk Response' },
      { id: 'risk-monitoring', label: 'Risk Monitoring' },
    ],
  },
  {
    section: 'Reports',
    items: [
      { id: 'reports', label: 'Generating Reports' },
    ],
  },
  {
    section: 'Administration',
    items: [
      { id: 'managing-users', label: 'Managing Users' },
      { id: 'managing-departments', label: 'Managing Departments' },
    ],
  },
  {
    section: 'Platform Setup',
    items: [
      { id: 'tenant-onboarding', label: 'Tenant Onboarding' },
    ],
  },
];

// ── Shared UI primitives ─────────────────────────────────────────────────────

function Callout({ type = 'note', title, children }) {
  const styles = {
    note:    { wrap: 'bg-blue-50 border-blue-300',   title: 'text-blue-800',   body: 'text-blue-700',   icon: 'ℹ' },
    tip:     { wrap: 'bg-emerald-50 border-emerald-300', title: 'text-emerald-800', body: 'text-emerald-700', icon: '✦' },
    warning: { wrap: 'bg-amber-50 border-amber-300',  title: 'text-amber-800',  body: 'text-amber-700',  icon: '!' },
    danger:  { wrap: 'bg-red-50 border-red-300',      title: 'text-red-800',    body: 'text-red-700',    icon: '✕' },
  };
  const s = styles[type] || styles.note;
  return (
    <div className={`border-l-[3px] ${s.wrap} rounded-r-lg px-4 py-3 my-5`}>
      {title && (
        <p className={`text-xs font-bold uppercase tracking-wide ${s.title} mb-1 flex items-center gap-1.5`}>
          <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[10px] shrink-0">{s.icon}</span>
          {title}
        </p>
      )}
      <p className={`text-sm leading-relaxed ${s.body}`}>{children}</p>
    </div>
  );
}

function Badge({ children, color = 'gray' }) {
  const map = {
    gray:   'bg-gray-100 text-gray-600 border-gray-200',
    teal:   'bg-[#3C6E71]/10 text-[#3C6E71] border-[#3C6E71]/20',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    blue:   'bg-blue-50 text-blue-700 border-blue-200',
    red:    'bg-red-50 text-red-700 border-red-200',
    green:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${map[color] || map.gray}`}>
      {children}
    </span>
  );
}

function Code({ children }) {
  return (
    <code className="bg-gray-100 text-[#3C6E71] px-1.5 py-0.5 rounded text-[13px] font-mono border border-gray-200">
      {children}
    </code>
  );
}

function H1({ children }) {
  return <h1 className="text-[2rem] font-bold text-gray-900 mb-2 tracking-tight leading-tight">{children}</h1>;
}

function H2({ children }) {
  return <h2 className="text-lg font-semibold text-gray-800 mt-10 mb-3 pb-2 border-b border-gray-100">{children}</h2>;
}

function P({ children }) {
  return <p className="text-gray-600 leading-relaxed mb-3 text-sm">{children}</p>;
}

function Ul({ items }) {
  return (
    <ul className="space-y-2 my-3 list-none">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3 items-start text-sm text-gray-600">
          <span className="w-1.5 h-1.5 rounded-full bg-[#3C6E71] mt-[7px] shrink-0" />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepList({ steps }) {
  return (
    <ol className="space-y-4 my-5">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-4">
          <div className="w-7 h-7 rounded-full bg-[#3C6E71] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
            {i + 1}
          </div>
          <div className="flex-1 pt-0.5">
            {step.title && <p className="text-sm font-semibold text-gray-800 mb-0.5">{step.title}</p>}
            <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="overflow-x-auto my-5 rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50/70 transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-gray-700 leading-relaxed align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionPill({ children, color = 'teal' }) {
  const map = {
    teal:   'bg-[#3C6E71]/10 text-[#3C6E71]',
    orange: 'bg-orange-100 text-orange-700',
  };
  return (
    <div className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full mb-4 ${map[color]}`}>
      {children}
    </div>
  );
}

// ── Page: Introduction ────────────────────────────────────────────────────────

function IntroductionPage() {
  return (
    <div>
      <SectionPill>Getting Started</SectionPill>
      <H1>Welcome to RiskFlow</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        A collaborative risk management platform that helps organisations identify, assess, respond to, and monitor risks — all in one place.
      </p>

      <Callout type="tip" title="New to RiskFlow?">
        Start with Key Concepts to understand how risks are structured, then jump to Risk Registration to log your first risk.
      </Callout>

      <H2>What is RiskFlow?</H2>
      <P>
        RiskFlow guides teams through the full risk lifecycle — from initial identification through to ongoing monitoring and reporting. It is built around the ISO 31000 risk management standard, adapted for practical day-to-day use.
      </P>
      <P>
        Risks are organised by <strong>Perspectives</strong> and <strong>Strategic Objectives</strong>, scored across standard dimensions (Impact, Likelihood, Control, and Acceptance), and tracked through treatment plans designed to reduce organisational exposure over time.
      </P>

      <H2>Who is it for?</H2>
      <Table
        headers={['Role', 'What they do in RiskFlow']}
        rows={[
          [<Badge color="blue">Employee</Badge>, 'Register risks, complete assessments, and track assigned treatment actions.'],
          [<Badge color="teal">Admin</Badge>, 'Manage all risks, users, and departments — and generate reports across the organisation.'],
          [<Badge color="orange">Super Admin</Badge>, 'Onboard and configure new client tenants on the platform (internal team only).'],
        ]}
      />

      <H2>How this documentation is organised</H2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
        {[
          { section: 'Getting Started', desc: 'Core concepts, roles, and how the platform is structured.' },
          { section: 'Risk Management', desc: 'Step-by-step guides for each stage of the risk lifecycle.' },
          { section: 'Reports', desc: 'How to generate, filter, and export risk data.' },
          { section: 'Administration', desc: 'Managing users and departments as an Admin.' },
          { section: 'Platform Setup', desc: 'Internal guide for onboarding new client tenants.' },
        ].map(({ section, desc }) => (
          <div key={section} className="border border-gray-200 rounded-xl p-4 hover:border-[#3C6E71]/30 hover:bg-gray-50 transition-colors">
            <p className="text-sm font-semibold text-gray-800 mb-1">{section}</p>
            <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page: Key Concepts ────────────────────────────────────────────────────────

function KeyConceptsPage() {
  return (
    <div>
      <SectionPill>Getting Started</SectionPill>
      <H1>Key Concepts</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        These core ideas underpin everything in RiskFlow. Spend five minutes here and the rest of the platform will make immediate sense.
      </p>

      <H2>Risk</H2>
      <P>
        A <strong>risk</strong> is any event, condition, or uncertainty that could affect the organisation's ability to achieve its objectives — positively or negatively. In RiskFlow, every risk is captured with a title, description, the context it relates to, and a scored assessment.
      </P>

      <H2>Perspectives & Strategic Objectives</H2>
      <P>Risks are categorised using two layers of context:</P>
      <Ul items={[
        'Perspective — The high-level risk category (e.g. Financial, Operational, Strategic, Reputational). Think of it as the lens through which you\'re viewing the risk.',
        'Strategic Objective (Factor) — A specific goal within a perspective that the risk could affect (e.g. \u201cIncrease Revenue\u201d under Financial, or \u201cMaintain Uptime\u201d under Operational).',
      ]} />
      <Callout type="note" title="Configured per tenant">
        Perspectives and objectives are set during tenant onboarding and reflect the specific strategic context of each organisation.
      </Callout>

      <H2>Scoring Dimensions</H2>
      <P>Every risk is evaluated on four dimensions, each rated on a 1–5 scale:</P>
      <Table
        headers={['Dimension', 'What it measures', '1 — Low', '5 — High']}
        rows={[
          ['Impact', 'How severely the risk would affect the organisation if it occurred', 'Insignificant', 'Severe'],
          ['Likelihood', 'How probable it is that the risk will materialise', 'Rare', 'Almost Certain'],
          ['Control', 'How effective current controls are at mitigating the risk', 'Weak / absent', 'Excellent'],
          ['Acceptance', 'How tolerable the residual risk level is to the organisation', 'Unacceptable', 'Fully acceptable'],
        ]}
      />
      <Callout type="note">
        Scale labels are configurable per tenant. Your organisation may use different terminology — the underlying 1–5 logic is the same.
      </Callout>

      <H2>Risk Score & Rating</H2>
      <P>
        The <strong>Risk Score</strong> = Impact × Likelihood. This number maps to a <strong>Risk Rating</strong> that drives prioritisation and reporting:
      </P>
      <Table
        headers={['Score', 'Rating', 'Guidance']}
        rows={[
          ['1 – 4',   <Badge color="green">Low</Badge>,    'Monitor periodically. No immediate treatment required.'],
          ['5 – 9',   <Badge color="blue">Medium</Badge>,  'Schedule treatment. Review quarterly.'],
          ['10 – 16', <Badge color="orange">High</Badge>,  'Prioritise for treatment. Review monthly.'],
          ['17 – 25', <Badge color="red">Critical</Badge>, 'Immediate action required. Escalate to senior management.'],
        ]}
      />

      <H2>Inherent vs Residual Risk</H2>
      <P>
        <strong>Inherent risk</strong> is the raw exposure before any controls are applied. <strong>Residual risk</strong> is what remains after controls are in place and treatment actions are executed. RiskFlow tracks both, so you can quantify how much your controls are actually reducing exposure.
      </P>
    </div>
  );
}

// ── Page: Roles ───────────────────────────────────────────────────────────────

function RolesPage() {
  return (
    <div>
      <SectionPill>Getting Started</SectionPill>
      <H1>Roles & Permissions</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        RiskFlow has three roles. Each gives a different level of access and responsibility.
      </p>

      <H2>Employee <Badge color="blue">Employee</Badge></H2>
      <P>
        Employees are the frontline risk contributors. They identify and register risks in their area, complete assessments, and action the treatment tasks assigned to them.
      </P>
      <Table
        headers={['Capability', 'Access']}
        rows={[
          ['Register a new risk', '✅ Yes'],
          ['View risks in their department', '✅ Yes'],
          ['Complete a risk assessment', '✅ Yes'],
          ['Add treatment actions', '✅ Yes'],
          ['View reports', '✅ Yes'],
          ['Manage other users', '❌ No'],
          ['Access admin settings', '❌ No'],
        ]}
      />

      <H2>Admin <Badge color="teal">Admin</Badge></H2>
      <P>
        Admins have full oversight of the organisation's risk portfolio. They review and approve risk registrations, manage the team, and produce reports for senior management.
      </P>
      <Table
        headers={['Capability', 'Access']}
        rows={[
          ['All Employee capabilities', '✅ Yes'],
          ['View all risks across the organisation', '✅ Yes'],
          ['Approve or return risk registrations', '✅ Yes'],
          ['Manage users (create, edit, deactivate)', '✅ Yes'],
          ['Manage departments', '✅ Yes'],
          ['Generate and export reports', '✅ Yes'],
          ['Edit platform settings', '✅ Yes'],
        ]}
      />

      <H2>Super Admin <Badge color="orange">Super Admin</Badge></H2>
      <P>
        Super Admins are internal platform operators at the RiskFlow team. They onboard new client tenants — configuring branding, risk settings, and seeding the first admin account. This role exists outside the normal tenant permission model.
      </P>
      <Callout type="warning" title="Internal use only">
        The Super Admin interface is for the RiskFlow team only and must never be shared with clients.
      </Callout>
    </div>
  );
}

// ── Page: Risk Registration ───────────────────────────────────────────────────

function RiskRegistrationPage() {
  return (
    <div>
      <SectionPill>Risk Management</SectionPill>
      <H1>Risk Registration</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        Risk registration is the formal process of recording a potential risk before it escalates. Any user with an account can register a risk.
      </p>

      <H2>How to Register a Risk</H2>
      <StepList steps={[
        { title: 'Open Risk Registration', desc: 'Click "Risk Registration" in the left sidebar.' },
        { title: 'Click "Add Risk"', desc: 'The risk form will slide open.' },
        { title: 'Describe the risk', desc: 'Enter a clear, concise title and a full description — including what could go wrong, what triggers it, and the potential consequences.' },
        { title: 'Set the context', desc: 'Select the Perspective (e.g. Financial) and the Strategic Objective it threatens (e.g. Increase Revenue). Assign a risk owner.' },
        { title: 'Score the inherent risk', desc: 'Rate Impact and Likelihood as if no controls exist. This establishes the raw baseline.' },
        { title: 'Document existing controls', desc: 'Describe any controls already in place and rate their effectiveness with the Control score.' },
        { title: 'Submit for review', desc: 'Click "Submit". The risk enters Pending status and is queued for Admin approval.' },
      ]} />

      <Callout type="note" title="What happens after submission?">
        An Admin reviews the registration and either approves it (adding it to the active risk register) or returns it with feedback for revision.
      </Callout>

      <H2>Required Fields</H2>
      <Table
        headers={['Field', 'Description', 'Required']}
        rows={[
          ['Risk Title', 'Short, clear name for the risk', '✅'],
          ['Description', 'Full explanation of the risk — what, why, and potential impact', '✅'],
          ['Perspective', 'High-level category (e.g. Financial, Operational)', '✅'],
          ['Strategic Objective', 'The specific goal at risk (e.g. Increase Revenue)', '✅'],
          ['Risk Owner', 'Person accountable for managing the risk', '✅'],
          ['Impact Score', '1–5, scored before controls are considered', '✅'],
          ['Likelihood Score', '1–5, scored before controls are considered', '✅'],
          ['Existing Controls', 'Description of current mitigations', 'Recommended'],
          ['Control Score', 'Effectiveness of existing controls (1–5)', 'Recommended'],
        ]}
      />

      <H2>Risk Status Flow</H2>
      <div className="flex items-center gap-2 flex-wrap my-4 text-sm">
        {['Pending', '→', 'Under Review', '→', 'Active', '→', 'Treated', '→', 'Closed'].map((s, i) => (
          s === '→'
            ? <span key={i} className="text-gray-300 text-lg">→</span>
            : <span key={i} className={`px-3 py-1 rounded-full text-xs font-medium border ${
                s === 'Active' ? 'bg-[#3C6E71]/10 text-[#3C6E71] border-[#3C6E71]/20' :
                s === 'Closed' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                'bg-blue-50 text-blue-700 border-blue-200'
              }`}>{s}</span>
        ))}
      </div>
    </div>
  );
}

// ── Page: Risk Assessment ─────────────────────────────────────────────────────

function RiskAssessmentPage() {
  return (
    <div>
      <SectionPill>Risk Management</SectionPill>
      <H1>Risk Assessment</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        Risk assessment is the structured evaluation of an identified risk — scoring its dimensions to determine the organisation's level of exposure and prioritise treatment.
      </p>

      <H2>The Four Dimensions</H2>
      <Table
        headers={['Dimension', 'Question to answer', 'Scale']}
        rows={[
          ['Impact', 'If this risk occurred today, how severe would the consequences be?', '1 (Insignificant) → 5 (Severe)'],
          ['Likelihood', 'How probable is it that this risk will materialise in the coming period?', '1 (Rare) → 5 (Almost Certain)'],
          ['Control', 'How effective are the controls currently in place?', '1 (Weak) → 5 (Excellent)'],
          ['Acceptance', 'Is the residual exposure within the organisation\'s risk appetite?', '1 (Unacceptable) → 5 (Excellent)'],
        ]}
      />

      <H2>Risk Score & Rating</H2>
      <P>
        <strong>Risk Score = Impact × Likelihood.</strong> Use this matrix as a reference:
      </P>
      <div className="my-5 overflow-x-auto">
        <table className="border-collapse text-xs">
          <thead>
            <tr>
              <th className="w-20 h-10 bg-gray-50 border border-gray-200 text-gray-400 font-normal px-2 text-left">L \ I</th>
              {[1, 2, 3, 4, 5].map(i => (
                <th key={i} className="w-14 h-10 bg-gray-50 border border-gray-200 text-gray-600 font-semibold px-2">{i}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[5, 4, 3, 2, 1].map(l => (
              <tr key={l}>
                <td className="bg-gray-50 border border-gray-200 text-gray-600 font-semibold px-2 py-2 text-center">{l}</td>
                {[1, 2, 3, 4, 5].map(i => {
                  const score = l * i;
                  const color = score >= 17 ? 'bg-red-100 text-red-800' : score >= 10 ? 'bg-orange-100 text-orange-800' : score >= 5 ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800';
                  return (
                    <td key={i} className={`border border-gray-200 text-center font-semibold py-2 ${color}`}>{score}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-400 mt-2">Rows = Likelihood (5 top), Columns = Impact (5 right)</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { label: 'Low (1–4)', color: 'green' },
          { label: 'Medium (5–9)', color: 'blue' },
          { label: 'High (10–16)', color: 'orange' },
          { label: 'Critical (17–25)', color: 'red' },
        ].map(({ label, color }) => <Badge key={label} color={color}>{label}</Badge>)}
      </div>

      <H2>Residual Risk Reassessment</H2>
      <P>
        After treatment actions are executed, the risk should be reassessed. The new Impact and Likelihood scores reflect the <strong>residual risk</strong> — what remains after controls. Repeating this cycle validates that treatment is working.
      </P>
      <Callout type="tip" title="Best practice">
        Always reassess after completing a treatment action. A drop in the Impact or Likelihood score is evidence that the action was effective.
      </Callout>
    </div>
  );
}

// ── Page: Risk Response ───────────────────────────────────────────────────────

function RiskResponsePage() {
  return (
    <div>
      <SectionPill>Risk Management</SectionPill>
      <H1>Risk Response</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        Risk response is the decision of how to handle a risk — and the execution and tracking of that decision through treatment actions.
      </p>

      <H2>Response Strategies</H2>
      <Table
        headers={['Strategy', 'When to use', 'Example']}
        rows={[
          ['Mitigate', 'The risk can be reduced to within appetite through controls or process changes.', 'Add access controls to reduce the likelihood of a data breach.'],
          ['Accept', 'The risk is within appetite and the cost of treatment outweighs the benefit.', 'Accept a minor delay risk on a low-priority project.'],
          ['Transfer', 'The financial or operational consequence can be shifted to a third party.', 'Purchase cyber liability insurance.'],
          ['Avoid', 'The risk is unacceptable and the activity causing it can be discontinued.', 'Exit a market segment that carries unacceptable regulatory risk.'],
        ]}
      />

      <H2>Creating a Treatment Plan</H2>
      <StepList steps={[
        { title: 'Choose a strategy', desc: 'Select Mitigate, Accept, Transfer, or Avoid based on the risk rating, cost of treatment, and the organisation\'s risk appetite.' },
        { title: 'Add treatment actions', desc: 'Break the response into concrete, assignable tasks. Each action has a title, description, owner, and due date.' },
        { title: 'Assign owners', desc: 'Every action must have a named owner who is accountable for completing it on time.' },
        { title: 'Set realistic due dates', desc: 'High and Critical risks should have short treatment horizons. Use milestones for longer treatment plans.' },
        { title: 'Track and update', desc: 'Owners update action status as work progresses. The risk record reflects the current treatment state in real time.' },
      ]} />

      <Callout type="warning" title="Overdue actions">
        Overdue treatment actions are flagged in the risk register and surfaced in Admin reports. Ensure owners are clear on their responsibilities and deadlines.
      </Callout>

      <H2>Closing a Treatment Action</H2>
      <P>
        Mark an action as complete once the work is done and the control is in place. After all actions in a treatment plan are closed, the risk should be reassessed to confirm the residual level is now within appetite.
      </P>
    </div>
  );
}

// ── Page: Risk Monitoring ─────────────────────────────────────────────────────

function RiskMonitoringPage() {
  return (
    <div>
      <SectionPill>Risk Management</SectionPill>
      <H1>Risk Monitoring</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        Risk monitoring keeps the risk register accurate and ensures controls remain effective as the organisation and its environment change.
      </p>

      <H2>Review Cadence</H2>
      <Table
        headers={['Rating', 'Recommended Frequency']}
        rows={[
          [<Badge color="red">Critical</Badge>, 'Monthly — or immediately after any trigger event.'],
          [<Badge color="orange">High</Badge>, 'Monthly.'],
          [<Badge color="blue">Medium</Badge>, 'Quarterly.'],
          [<Badge color="green">Low</Badge>, 'Annually, or following a significant change in context.'],
        ]}
      />

      <H2>What to check in each review</H2>
      <Ul items={[
        'Have the Impact or Likelihood scores changed since the last assessment?',
        'Are existing controls still effective? Has the environment around them changed?',
        'Have all treatment actions been completed on time?',
        'Has the risk come close to materialising, or has a near-miss occurred?',
        'Should the risk be escalated, de-escalated, transferred, or closed?',
        'Are the risk owner and their department still accurate?',
      ]} />

      <H2>Trigger Events</H2>
      <P>
        Certain events should prompt an <em>immediate</em> review regardless of the scheduled cycle — for example: a regulatory change, a security incident, a restructure, a new product launch, or any event that materially changes the risk context.
      </P>

      <H2>Closing a Risk</H2>
      <P>
        A risk can be closed when it is no longer relevant — the triggering activity has ended, residual risk is within appetite with no further actions required, or the risk has been fully transferred or avoided.
      </P>
      <Callout type="note">
        Closed risks are retained in the register for audit and historical reporting. They can be reopened if circumstances change.
      </Callout>
    </div>
  );
}

// ── Page: Reports ─────────────────────────────────────────────────────────────

function ReportsPage() {
  return (
    <div>
      <SectionPill>Reports</SectionPill>
      <H1>Generating Reports</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        RiskFlow's built-in reporting gives Admins a real-time view of the organisation's risk portfolio — ready to export for board packs, audits, or management reviews.
      </p>

      <Callout type="note">
        Reports are available to <Badge color="teal">Admin</Badge> users only.
      </Callout>

      <H2>How to Generate a Report</H2>
      <StepList steps={[
        { title: 'Open Reports', desc: 'Click "Reports" in the left sidebar of the Admin dashboard.' },
        { title: 'Select a report type', desc: 'Choose from the available templates — Risk Register, Heat Map, Treatment Status, and more.' },
        { title: 'Apply filters', desc: 'Narrow results by Perspective, Department, Risk Rating, Date Range, or Owner.' },
        { title: 'Preview', desc: 'The report renders in the browser. Review the data before exporting.' },
        { title: 'Export', desc: 'Use the Export button (top right) to download as PDF or Excel.' },
      ]} />

      <H2>Available Reports</H2>
      <Table
        headers={['Report', 'Description', 'Best for']}
        rows={[
          ['Risk Register', 'Full list of all active risks with scores, owners, and status', 'Audits, management reviews'],
          ['Risk Heat Map', 'Visual Impact × Likelihood matrix across the portfolio', 'Board presentations'],
          ['Treatment Status', 'All open treatment actions and their completion progress', 'Operational risk reviews'],
          ['Risk by Department', 'Breakdown of risk exposure per department', 'Department heads'],
          ['Overdue Actions', 'Treatment actions that have passed their due date', 'Weekly risk meetings'],
        ]}
      />

      <H2>Export Formats</H2>
      <Ul items={[
        'PDF — Formatted, print-ready report. Ideal for board packs and presentations.',
        'Excel — Raw data export for pivot tables, custom analysis, or archiving.',
      ]} />

      <Callout type="tip" title="Scheduled reports">
        Admins can configure reports to be automatically generated and emailed to stakeholders on a recurring schedule — weekly, monthly, or quarterly.
      </Callout>
    </div>
  );
}

// ── Page: Managing Users ──────────────────────────────────────────────────────

function ManagingUsersPage() {
  return (
    <div>
      <SectionPill>Administration</SectionPill>
      <H1>Managing Users</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        Admins create and manage all user accounts within their organisation. Users can be employees or fellow admins.
      </p>

      <H2>Creating a New User</H2>
      <StepList steps={[
        { title: 'Go to Users', desc: 'In the Admin sidebar, click "User Management" or "Users".' },
        { title: 'Click "Add User"', desc: 'The user creation form opens.' },
        { title: 'Fill in user details', desc: 'Enter first name, last name, and email address. Assign them to a department.' },
        { title: 'Assign a role', desc: 'Choose Employee (standard access) or Admin (full platform access). Assign Admin sparingly.' },
        { title: 'Set a temporary password', desc: 'The user is prompted to change it on first login. Share the password securely.' },
        { title: 'Save', desc: 'The user is created and can log in immediately.' },
      ]} />

      <H2>Editing a User</H2>
      <P>
        Click any user in the list to open their profile. You can update their name, department, or role. Email addresses cannot be changed after account creation — contact support if a correction is needed.
      </P>

      <H2>Deactivating a User</H2>
      <P>
        Deactivated users cannot log in, but all their data — risks, assessments, and actions — is preserved and can be reassigned. To deactivate, open the user's profile and toggle the <strong>Active</strong> switch.
      </P>
      <Callout type="warning">
        You cannot deactivate your own account. At least one active Admin must remain in the organisation at all times.
      </Callout>

      <H2>Resetting a Password</H2>
      <P>
        Admins can trigger a password reset for any user from their profile. The user receives a reset link by email. Users can also self-serve by clicking <strong>Forgot Password</strong> on the login page.
      </P>
    </div>
  );
}

// ── Page: Managing Departments ────────────────────────────────────────────────

function ManagingDepartmentsPage() {
  return (
    <div>
      <SectionPill>Administration</SectionPill>
      <H1>Managing Departments</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        Every user must belong to a department. Departments segment risk data across the organisation and make reports meaningful.
      </p>

      <H2>Creating a Department</H2>
      <StepList steps={[
        { title: 'Go to Departments', desc: 'In the Admin sidebar, click "Departments".' },
        { title: 'Click "Add Department"', desc: 'Enter a department name and an optional description.' },
        { title: 'Save', desc: 'The department is immediately available for assigning users.' },
      ]} />

      <H2>Assigning Users to a Department</H2>
      <P>
        A department is assigned when a user is created. To move a user, edit their profile and change the Department field. The change takes effect immediately.
      </P>

      <H2>Renaming a Department</H2>
      <P>
        Click the department name in the list to edit it. The new name is reflected everywhere, including historical records and reports.
      </P>

      <H2>Deleting a Department</H2>
      <P>
        A department can only be deleted if it has no active users. Reassign or deactivate all users in the department first, then delete it.
      </P>
      <Callout type="note">
        Risks are linked to the department of their registrant at the time of registration. Moving a user to a new department does not retroactively update historical risk records.
      </Callout>
    </div>
  );
}

// ── Page: Tenant Onboarding ───────────────────────────────────────────────────

function TenantOnboardingPage() {
  return (
    <div>
      <SectionPill color="orange">Internal — Platform Setup</SectionPill>
      <H1>Tenant Onboarding</H1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">
        Tenant onboarding is the process of setting up a new client organisation on RiskFlow. This is done entirely by the RiskFlow team — not by the client.
      </p>
      <Callout type="warning" title="Internal use only">
        The onboarding tool at <Code>/superadmin/onboard-tenant</Code> is for internal use only. Never share the URL or platform key with clients.
      </Callout>

      <H2>Before You Start</H2>
      <P>Gather the following from the client before opening the onboarding tool:</P>
      <Ul items={[
        'Company name and the domain(s) users will access the app from',
        'Primary and secondary brand colours (hex codes preferred)',
        'Company logo file — PNG or SVG, ideally transparent background',
        'List of Perspectives (e.g. Financial, Operational) and Strategic Objectives under each',
        'First admin\'s full name and work email address',
      ]} />

      <H2>Onboarding Walkthrough</H2>
      <StepList steps={[
        { title: 'Open the onboarding tool', desc: 'Navigate to /superadmin/onboard-tenant and enter the platform key to unlock the form.' },
        { title: 'Step 1 — Company Info', desc: 'Enter the company name, domain(s), and logo URL (relative to the public/ folder, e.g. /acmeLogo.png).' },
        { title: 'Step 2 — Branding', desc: 'Set the primary and secondary colours. Use the live preview to confirm they look correct before continuing.' },
        { title: 'Step 3 — Risk Configuration', desc: 'Add the client\'s Perspectives and Strategic Objectives. Scoring scale labels default to standard ISO 31000 terminology — only change them if the client uses different language.' },
        { title: 'Step 4 — Admin Account', desc: 'Enter the first admin\'s full name, email, a secure temporary password, and the name of the first department.' },
        { title: 'Step 5 — Review & Submit', desc: 'Check all details on the Review screen, then click "Create Tenant". The backend creates the tenant, department, and admin account in one operation.' },
      ]} />

      <H2>After Submission</H2>
      <P>
        The tool automatically downloads a <Code>.json</Code> config file for the new tenant. Complete these steps before the client goes live:
      </P>
      <StepList steps={[
        { title: 'Add the config file', desc: 'Move the downloaded .json file into src/configs/ in the frontend repository.' },
        { title: 'Register the domain', desc: 'Add the domain → config file mapping to STATIC_CONFIG_MAP in src/utils/configLoader.js.' },
        { title: 'Add the logo', desc: 'Place the client\'s logo file in public/ using the filename that matches the Logo URL you entered during onboarding.' },
        { title: 'Deploy to staging', desc: 'Push to develop. Open the staging URL and verify that the branding loads correctly.' },
        { title: 'Share credentials securely', desc: 'Send the admin\'s temporary password through a secure channel (not email plain text). Remind them to change it on first login.' },
        { title: 'Go live', desc: 'Merge develop → main to deploy to production.' },
      ]} />

      <Callout type="tip" title="A note on scoring scale labels">
        The defaults (Insignificant → Severe, Rare → Almost Certain, etc.) work for the vast majority of clients. Only customise them if the client has an existing risk framework that uses different terminology — changing them just to change them creates unnecessary inconsistency.
      </Callout>
    </div>
  );
}

// ── Page map ──────────────────────────────────────────────────────────────────

const PAGE_MAP = {
  'introduction':         IntroductionPage,
  'key-concepts':         KeyConceptsPage,
  'roles':                RolesPage,
  'risk-registration':    RiskRegistrationPage,
  'risk-assessment':      RiskAssessmentPage,
  'risk-response':        RiskResponsePage,
  'risk-monitoring':      RiskMonitoringPage,
  'reports':              ReportsPage,
  'managing-users':       ManagingUsersPage,
  'managing-departments': ManagingDepartmentsPage,
  'tenant-onboarding':    TenantOnboardingPage,
};

// ── Docs layout ───────────────────────────────────────────────────────────────

export default function Docs() {
  const [active, setActive]         = useState('introduction');
  const [mobileOpen, setMobileOpen] = useState(false);

  const PageComponent = PAGE_MAP[active] || IntroductionPage;
  const flatItems     = NAV.flatMap(s => s.items);
  const activeIdx     = flatItems.findIndex(i => i.id === active);
  const prev          = flatItems[activeIdx - 1];
  const next          = flatItems[activeIdx + 1];

  function navigate(id) {
    setActive(id);
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">

      {/* ── Top bar ── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200 h-14 flex items-center px-4 md:px-6 gap-4">
        {/* Mobile menu */}
        <button
          className="md:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Brand */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-[#3C6E71] flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <span className="font-bold text-gray-900 text-sm">RiskFlow</span>
          <span className="text-gray-300 hidden sm:block">|</span>
          <span className="text-gray-500 text-sm hidden sm:block">Documentation</span>
          <span className="text-[10px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded hidden sm:block">v0.1</span>
        </div>

        <div className="flex-1" />

        {/* Search (decorative) */}
        <div className="hidden md:flex items-center gap-2 bg-gray-100 hover:bg-gray-200 transition-colors rounded-lg px-3 py-1.5 text-sm text-gray-400 w-48 cursor-pointer">
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-xs">Search docs...</span>
          <span className="ml-auto text-[10px] bg-white border border-gray-200 rounded px-1 py-0.5 font-mono text-gray-400">⌘K</span>
        </div>

        {/* Back to app */}
        <a
          href="/login"
          className="hidden md:flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-[#3C6E71] transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to app
        </a>
      </header>

      <div className="flex flex-1 relative">

        {/* ── Sidebar ── */}
        <aside className={`
          fixed md:sticky top-14 z-20
          w-64 shrink-0 h-[calc(100vh-3.5rem)]
          bg-white border-r border-gray-200 overflow-y-auto
          transition-transform duration-200 ease-in-out
          ${mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full md:translate-x-0'}
        `}>
          <nav className="px-3 py-6 space-y-6">
            {NAV.map(({ section, items }) => (
              <div key={section}>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3 mb-2">
                  {section}
                </p>
                <ul className="space-y-0.5">
                  {items.map(({ id, label }) => (
                    <li key={id}>
                      <button
                        onClick={() => navigate(id)}
                        className={`w-full text-left text-sm px-3 py-1.5 rounded-lg transition-all duration-100 flex items-center gap-2 ${
                          active === id
                            ? 'bg-[#3C6E71]/10 text-[#3C6E71] font-semibold'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        {active === id && (
                          <span className="w-1 h-1 rounded-full bg-[#3C6E71] shrink-0" />
                        )}
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-10 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* ── Main content ── */}
        <main className="flex-1 min-w-0">
          <div className="max-w-2xl mx-auto px-6 md:px-10 py-10">

            <PageComponent onNavigate={navigate} />

            {/* Prev / Next */}
            <div className="flex gap-3 mt-16 pt-8 border-t border-gray-100">
              {prev ? (
                <button
                  onClick={() => navigate(prev.id)}
                  className="flex-1 flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-[#3C6E71]/40 hover:bg-gray-50/80 transition-all text-left group"
                >
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-[#3C6E71] shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <div>
                    <p className="text-[10px] text-gray-400 mb-0.5 uppercase tracking-wide">Previous</p>
                    <p className="text-sm font-medium text-gray-700 group-hover:text-[#3C6E71] transition-colors">{prev.label}</p>
                  </div>
                </button>
              ) : <div className="flex-1" />}

              {next ? (
                <button
                  onClick={() => navigate(next.id)}
                  className="flex-1 flex items-center justify-end gap-3 p-4 rounded-xl border border-gray-200 hover:border-[#3C6E71]/40 hover:bg-gray-50/80 transition-all text-right group"
                >
                  <div>
                    <p className="text-[10px] text-gray-400 mb-0.5 uppercase tracking-wide">Next</p>
                    <p className="text-sm font-medium text-gray-700 group-hover:text-[#3C6E71] transition-colors">{next.label}</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-[#3C6E71] shrink-0 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : <div className="flex-1" />}
            </div>

            <p className="text-center text-xs text-gray-300 mt-10">
              RiskFlow Documentation · v0.1
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
